<template>
  <form class="sm-new-card" 
    @submit.prevent="handleSubmit"
  >
    <h4>{{title}}</h4>
    <credit-card
      name="credit_card"
      v-model="card"
      v-validate
      :error="errors.first('credit_card')"
    />

    <div class="sm-new-card__form">
      <input-text
        v-model="address.name"
        :value="address.name"
        :autocomplete="`cc-name`"
        :label="'Name on card'"
      />
      <input-text
        v-model="address.street"
        :autocomplete="`street-address`"
        :value="address.street"
        :label="'Street'"
      />

      <div class="sm-input-text-wrap">
        <label>Country</label>
        <dropdown-block
          v-model="selectedCountry"
          :preselectFirst="true"
          class="sm-dropdown scroll-y"
          :options="countryList"
          :searchable="false"
          :preselect-first="true"
        >
          <template slot="singleLabel" slot-scope="props" :value="props.option.id">
            <span>
              <include-flag :country="props.option.name" />
              {{ props.option.name }}
            </span>
            <include-svg :name="'icon-arrow-b'" />
          </template>
          <template slot="noOptions">
            <span>Loading...</span>
          </template>
          <template slot="option" slot-scope="props" :value="props.option.id">
            <include-flag :country="props.option.name" />
            <span class="sm-txt-capitalize">{{ props.option.name }}</span>
          </template>
        </dropdown-block>
      </div>

      <input-text
        v-model="address.city"
        :autocomplete="`address-level2`"
        :value="address.city"
        :label="'City'"
      />

      <div
        v-if="stateList.length" 
        class="sm-input-text-wrap"
      >
        <label>State</label>
        <dropdown-block
          v-model="selectedState"
          :preselectFirst="true"
          class="sm-dropdown scroll-y"
          :options="stateList"
          :searchable="false"
          :preselect-first="true"
        >
          <template slot="placeholder">
            <span>Select state</span>
            <include-svg :name="'icon-arrow-b'" />
          </template>
          <template slot="singleLabel" slot-scope="props">
            <span>{{ props.option.name }}</span>
            <include-svg :name="'icon-arrow-b'" />
          </template>
          <template slot="option" slot-scope="props" :value="props.option.id">
            <span>{{props.option.name}}</span>
          </template>
        </dropdown-block>
      </div>

      <input-zip
        v-model="address.zip"
        v-validate="'required|is_zip'"
        :country="selectedCountry"
        :name="'zip'"
        :error="errors.first('zip')"
      />
    </div>
    <p class="sm-new-card__footer sm-txt-center">
      <btn-block 
        type="submit" 
        :text="btnText" 
        :class="{'disabled': disableSubmit }" 
      />
    </p>
  </form>
</template>

<script>
import FormDropdowns from '@/mixins/FormDropdowns';
import InputText from '@/components/InputText';
import CreditCard from '@/components/credit-card/CreditCard';
import InputZip from '@/components/InputZip';
import { mapGetters } from 'vuex';

export default {
  name: 'new-card',
  components: {
    'input-text': InputText,
    'credit-card': CreditCard,
    'input-zip': InputZip,
  },
  props: {
    title: {
      type: String,
      default: 'Add new card'
    },
    btnText: {
      type: String,
      default: 'Add card'
    },
    accountProp: {
      type: Object,
      default: () => new Object()
    }
  },
  data() {
    return {
      card: null,
      defaultCard: {
        number: '',
        expiration: {
          month: '',
          year: ''
        },
        cvc: ''
      },
      address: null,
      defaultAddress: {
        name: '',
        street: '',
        city: '',
        country: '',
        state: '',
        zip: ''
      }
    };
  },
  created() {
    this.card = this.defaultCard;
    this.address = this.defaultAddress;

    Object.keys(this.defaultAddress).forEach(key => {
      if (key == 'name' && this.userAccount.first_name && this.userAccount.last_name)
        this.address[key] = `${this.userAccount.first_name} ${this.userAccount.last_name}`;

      if (key == 'zip' && this.userAccount.zip_code)
        this.address[key] = this.userAccount.zip_code;

      if (this.userAccount[key])
        this.address[key] = this.userAccount[key];
    });

    this.mixinUserAccount = this.userAccount;
  },
  computed: {
    ...mapGetters({
      currentUserAccount: 'account/userAccount',
    }),
    disableSubmit() {
      return (
        Object.values(this.address).some(item => !item) ||
        Object.values(this.card).some(item => !item) ||
        this.errors.items.length
      );
    },
    userAccount() {
      return Object.keys(this.accountProp).length ? this.accountProp : this.currentUserAccount;
    }
  },
  methods: {
    handleSubmit() {
      if(this.disableSubmit) return false;
      this.$emit('create', this.card, this.address);
    }
  },
  mixins: [FormDropdowns],
  watch: {
    selectedCountry(val) {
      this.address.country = val;
    },
    selectedState(val) {
      if (val)
        this.address.state = val;
    },
    userAccount(val) {
      this.mixinUserAccount = val;
    }
  }
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;

  .sm-new-card {
    & > .sm-credit-card {
      margin-top: 24px;
    }
    
    &__form {
      padding-top: 14px;

      display: flex;
      flex-wrap: wrap;
      margin: 0 -10px;
      width: auto;

      & > .sm-input-text-wrap {
        flex: 0 0 50%;
        max-width: 50%;
        padding: 10px;

        & > .sm-input-text-error {
          left: 10px;
          bottom: -12px;
        }
      }

      & > .sm-input-text-wrap {
        width: 300px;
      }

      & > button {
        margin: 20px var(--gap) 0 0;
      }
    }

    &__footer {
      margin-top: 40px;
    }

    @include media('<=tiny') {
      &__footer {
        margin-top: 32px;

        & > button {
          width: 100%;
        }
      }
    }
  }
</style>
